import React, {
    Fragment,
    forwardRef,
    useEffect,
    useId,
    useRef,
    useState,
} from "react";

import {
    SplitPanelCardBlock,
    SplitPanelCardsBlock as Value,
} from "@reactivated";

import { useCurrentBreakpoint } from "../../utils/hooks";
import { MediaChooserBlock } from "../MediaChooserBlock";

import styles from "./index.module.scss";

export const SplitPanelCard = forwardRef<HTMLDivElement, SplitPanelCardBlock>(
    ({ detail_media: media, title, subtitle }, ref) => {
        return (
            <div className={styles.card} ref={ref}>
                {media && media.length > 0 ? (
                    <div className={styles.cardMedia}>
                        <MediaChooserBlock value={media} type="background" />
                    </div>
                ) : null}
                <h3 className={styles.cardTitle}>{title}</h3>
                <p className={styles.cardSubtitle}>{subtitle}</p>
            </div>
        );
    },
);

interface Props {
    value: Value;
}

export const SplitPanelCardsBlock = ({ value: { cards, theme } }: Props) => {
    const id = useId();
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [visibleIndex, setVisibleIndex] = useState<number>(-1);
    const viewport = useCurrentBreakpoint();

    useEffect(() => {
        const updateObserver = () => {
            const threshold = viewport.belowMobile ? 0.75 : 0.25;

            observerRef.current?.disconnect();

            const observer = new IntersectionObserver(
                (entries) =>
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const index = cardRefs.current.findIndex(
                                (element) => element === entry.target,
                            );
                            setVisibleIndex(index);
                        }
                    }),
                { threshold },
            );

            cardRefs.current.forEach((ref) => {
                if (ref) observer.observe(ref);
            });

            observerRef.current = observer;
        };

        updateObserver();
        window.addEventListener("resize", updateObserver);

        return () => {
            observerRef.current?.disconnect();
            window.removeEventListener("resize", updateObserver);
        };
    }, []);

    if (!cards) return <></>;

    const featureMedia = cards.map((card) => card.feature_media);

    return (
        <section className={styles[theme ?? "match"]}>
            <div className={styles.featureWrapper}>
                {featureMedia.map((media, index) =>
                    media ? (
                        <MediaChooserBlock
                            key={media[0].id}
                            value={media}
                            type="background"
                            wrapperAttrs={{
                                className:
                                    visibleIndex >= index
                                        ? styles.featureMediaVisible
                                        : styles.featureMedia,
                            }}
                            mediaAttrs={
                                {
                                    loading:
                                        media[0].type === "image"
                                            ? "eager"
                                            : undefined,
                                } as React.ImgHTMLAttributes<HTMLImageElement>
                            }
                        />
                    ) : (
                        <Fragment key={`${id}-featured-${index}`}></Fragment>
                    ),
                )}
            </div>
            <div className={styles.wrapper}>
                {cards.map((card, index) => (
                    <SplitPanelCard
                        ref={(element) => {
                            cardRefs.current[index] = element;
                        }}
                        key={`${id}-card-${index}`}
                        {...card}
                    />
                ))}
            </div>
        </section>
    );
};
